import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  FaBolt,
  FaBrain,
  FaChartBar,
  FaCheckCircle,
  FaCloud,
  FaCogs,
  FaDatabase,
  FaEye,
  FaMagic,
  FaRobot,
  FaTimesCircle,
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';

/**
 * Real SHAP top drivers from a Total Bases projection (Carson Williams, Aug 25, 2025).
 * Batting order omitted to match in-app Model Insights / POTD display rules.
 */
const REAL_SHAP_EXAMPLE = {
  playerName: 'Carson Williams',
  team: 'Tampa Bay',
  modelLabel: 'Total Bases',
  dateLabel: 'Aug 25, 2025',
  baseValue: 1.177,
  features: [
    {
      feature: 'Park Factor',
      value: -0.0936,
      label: 'Ballpark total-bases environment',
    },
    {
      feature: 'Career Splits',
      value: 0.0865,
      label: 'Hitter career splits vs pitcher handedness',
    },
    {
      feature: 'Expected Avg',
      value: 0.0553,
      label: 'Recent expected batting average (m_xBA)',
    },
    {
      feature: 'Weather',
      value: -0.0512,
      label: 'Wind / weather effect on the game',
    },
    {
      feature: 'Hot Streak',
      value: 0.0334,
      label: 'Recent total bases per PA hot streak (r_TB/PA)',
    },
  ],
};

const PIPELINE_STEPS = [
  {
    id: 'fetch',
    icon: FaDatabase,
    title: 'Fetch',
    subtitle: 'Live data Lambdas',
    detail: 'Stats, lineups, and multi-book odds are pulled on a schedule so projections always reflect today\'s slate.',
  },
  {
    id: 'engineer',
    icon: FaCogs,
    title: 'Engineer',
    subtitle: 'Proprietary features',
    detail: 'Raw feeds become custom matchup, form, and context features that generic chatbots never see.',
  },
  {
    id: 'predict',
    icon: FaBrain,
    title: 'Predict',
    subtitle: 'XGBoost models',
    detail: 'Sport-specific gradient boosting models score each prop with a calibrated probability—not a vibe.',
  },
  {
    id: 'explain',
    icon: FaEye,
    title: 'Explain',
    subtitle: 'SHAP insights',
    detail: 'Every projection ships with SHAP values so you can see which features drove the call.',
  },
  {
    id: 'edge',
    icon: FaBolt,
    title: 'Edge',
    subtitle: 'Odds integration',
    detail: 'Model probability is compared to live market odds to surface where we believe value exists.',
  },
  {
    id: 'curate',
    icon: FaMagic,
    title: 'Curate',
    subtitle: 'LLM top picks',
    detail: 'An LLM inspects the full pipeline output to highlight Picks and Parlays of the Day—no homework required.',
  },
];

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.35);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(66, 153, 225, 0);
  }
`;

const flowPulse = keyframes`
  0% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.35;
  }
`;

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
  min-height: 80vh;
`;

const Hero = styled.header`
  text-align: center;
  margin-bottom: 3.5rem;
  padding: 2.5rem 1.5rem;
  border-radius: 16px;
  background:
    radial-gradient(ellipse at 20% 0%, rgba(66, 153, 225, 0.22), transparent 55%),
    radial-gradient(ellipse at 80% 100%, rgba(72, 187, 120, 0.12), transparent 50%),
    ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  animation: ${fadeUp} 0.6s ease both;
`;

const Eyebrow = styled.p`
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(props) => props.theme.primary};
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 700;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 1.15rem;
  color: ${(props) => props.theme.secondaryText};
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.65;
`;

const Section = styled.section`
  margin-bottom: 4rem;
  animation: ${fadeUp} 0.55s ease both;
  animation-delay: ${(props) => props.$delay || '0s'};
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  text-align: center;
  margin-bottom: 0.75rem;
`;

const SectionLead = styled.p`
  text-align: center;
  color: ${(props) => props.theme.secondaryText};
  max-width: 680px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const PipelineTrack = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PipelineStep = styled.button`
  appearance: none;
  border: 1px solid ${(props) => (props.$active ? props.theme.primary : props.theme.border)};
  background: ${(props) => (props.$active ? 'rgba(66, 153, 225, 0.15)' : props.theme.secondaryBg)};
  border-radius: 12px;
  padding: 1rem 0.75rem;
  cursor: pointer;
  text-align: center;
  color: inherit;
  transition: border-color 0.25s ease, background 0.25s ease, transform 0.25s ease;
  animation: ${(props) => (props.$active ? pulseGlow : 'none')} 2s ease infinite;

  &:hover {
    transform: translateY(-3px);
    border-color: ${(props) => props.theme.primary};
  }
`;

const StepIcon = styled.div`
  width: 40px;
  height: 40px;
  margin: 0 auto 0.65rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.$active ? props.theme.primary : props.theme.tertiaryBg)};
  color: white;
  font-size: 1rem;
`;

const StepTitle = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.2rem;
`;

const StepSubtitle = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.mutedText};
`;

const PipelineDetail = styled.div`
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  color: ${(props) => props.theme.secondaryText};
  line-height: 1.6;
  min-height: 4.5rem;
`;

const FlowConnector = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.35rem;
  margin: 0.75rem 0 1.25rem;
`;

const FlowDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => props.theme.primary};
  animation: ${flowPulse} 1.4s ease-in-out infinite;
  animation-delay: ${(props) => props.$delay};
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: ${(props) => props.theme.shadow};
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const CardBody = styled.p`
  color: ${(props) => props.theme.secondaryText};
  line-height: 1.65;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Pill = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  background: rgba(66, 153, 225, 0.15);
  border: 1px solid rgba(66, 153, 225, 0.35);
  border-radius: 999px;
  padding: 0.3rem 0.7rem;
`;

const ChartCard = styled(Card)`
  min-height: 360px;
`;

const ChartCaption = styled.p`
  font-size: 0.85rem;
  color: ${(props) => props.theme.mutedText};
  text-align: center;
  margin-top: 0.75rem;
  line-height: 1.45;
`;

const EdgeVisual = styled.div`
  display: grid;
  gap: 1rem;
`;

const EdgeBarRow = styled.div``;

const EdgeLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: ${(props) => props.theme.secondaryText};
  margin-bottom: 0.4rem;
`;

const EdgeTrack = styled.div`
  height: 12px;
  border-radius: 999px;
  background: ${(props) => props.theme.tertiaryBg};
  overflow: hidden;
`;

const EdgeFill = styled.div`
  height: 100%;
  width: ${(props) => props.$width};
  border-radius: 999px;
  background: ${(props) => props.$color};
  transition: width 0.8s ease;
`;

const EdgeCallout = styled.div`
  margin-top: 0.5rem;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  background: rgba(72, 187, 120, 0.12);
  border: 1px solid rgba(72, 187, 120, 0.35);
  color: ${(props) => props.theme.success};
  font-weight: 600;
  font-size: 0.95rem;
  text-align: center;
`;

const CompareGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CompareColumn = styled.div`
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => (props.$accent === 'good' ? 'rgba(72, 187, 120, 0.45)' : props.theme.border)};
  border-radius: 12px;
  padding: 1.5rem;
`;

const CompareHeading = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CompareList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CompareItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid ${(props) => props.theme.border};
  color: ${(props) => props.theme.secondaryText};
  line-height: 1.45;
  font-size: 0.95rem;

  &:last-child {
    border-bottom: none;
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.2rem;
  }
`;

const InfraGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`;

const InfraNode = styled.div`
  background: ${(props) => props.theme.secondaryBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 10px;
  padding: 1.1rem;
  text-align: center;
  transition: transform 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${(props) => props.theme.primary};
  }
`;

const InfraIcon = styled.div`
  color: ${(props) => props.theme.primary};
  font-size: 1.35rem;
  margin-bottom: 0.5rem;
`;

const InfraTitle = styled.div`
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  font-size: 0.95rem;
  margin-bottom: 0.35rem;
`;

const InfraDesc = styled.div`
  font-size: 0.8rem;
  color: ${(props) => props.theme.mutedText};
  line-height: 1.4;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
`;

const CtaSection = styled.div`
  text-align: center;
  background:
    linear-gradient(135deg, rgba(43, 108, 176, 0.35), rgba(66, 153, 225, 0.15)),
    ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 16px;
  padding: 2.75rem 1.5rem;
`;

const CtaTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.75rem;
`;

const CtaText = styled.p`
  color: ${(props) => props.theme.secondaryText};
  max-width: 560px;
  margin: 0 auto 1.5rem;
  line-height: 1.6;
`;

const CtaButton = styled.button`
  background: ${(props) => props.theme.primary};
  color: white;
  border: none;
  padding: 0.95rem 1.75rem;
  font-size: 1.05rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${(props) => props.theme.primaryHover};
  }
`;

const SecondaryLink = styled.button`
  display: block;
  margin: 1rem auto 0;
  background: transparent;
  border: none;
  color: ${(props) => props.theme.primary};
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

/**
 * Marketing page explaining the Slick Picks prediction pipeline.
 *
 * Covers proprietary features, XGBoost models, SHAP explainability,
 * live odds edge, Lambda infrastructure, and LLM curation vs. chatbots.
 *
 * @returns {JSX.Element} The How it works page.
 */
const HowItWorksPage = () => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [edgeReady, setEdgeReady] = useState(false);

  usePageTitle('How It Works – XGBoost Models, SHAP & Live Odds');

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % PIPELINE_STEPS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setEdgeReady(true), 400);
    return () => clearTimeout(timer);
  }, []);

  const handleGetStarted = () => {
    navigate(isAuthenticated ? '/dashboard' : '/register');
  };

  const active = PIPELINE_STEPS[activeStep];
  const ActiveIcon = active.icon;

  return (
    <Page>
      <PageMeta description="See how Slick Picks works: proprietary MLB feature engineering, XGBoost models, SHAP explainability, live odds edge detection, and LLM-curated Picks of the Day—built on a real data pipeline, not a chatbot prompt." />

      <Hero theme={theme}>
        <Eyebrow theme={theme}>Inside the engine</Eyebrow>
        <Title theme={theme}>How Slick Picks Works</Title>
        <Subtitle theme={theme}>
          Predictions are produced by a living data pipeline—proprietary stats, custom
          feature engineering, XGBoost models, SHAP explanations, and live odds—then
          curated into clear top picks. Not a one-off ChatGPT prompt.
        </Subtitle>
      </Hero>

      <Section $delay="0.05s">
        <SectionTitle theme={theme}>The daily pipeline</SectionTitle>
        <SectionLead theme={theme}>
          Tap a stage to explore it. The flow runs automatically so today&apos;s slate
          is scored with fresh data—not yesterday&apos;s guesswork.
        </SectionLead>

        <PipelineTrack>
          {PIPELINE_STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === activeStep;
            return (
              <PipelineStep
                key={step.id}
                type="button"
                theme={theme}
                $active={isActive}
                onClick={() => setActiveStep(index)}
                aria-pressed={isActive}
              >
                <StepIcon theme={theme} $active={isActive}>
                  <Icon aria-hidden />
                </StepIcon>
                <StepTitle theme={theme}>{step.title}</StepTitle>
                <StepSubtitle theme={theme}>{step.subtitle}</StepSubtitle>
              </PipelineStep>
            );
          })}
        </PipelineTrack>

        <FlowConnector>
          <FlowDot theme={theme} $delay="0s" />
          <FlowDot theme={theme} $delay="0.2s" />
          <FlowDot theme={theme} $delay="0.4s" />
        </FlowConnector>

        <PipelineDetail theme={theme}>
          <strong style={{ color: theme.primaryText, display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveIcon aria-hidden />
            {active.title}: {active.subtitle}
          </strong>
          <div style={{ marginTop: '0.5rem' }}>{active.detail}</div>
        </PipelineDetail>
      </Section>

      <Section $delay="0.1s">
        <SectionTitle theme={theme}>Built for edge, not vibes</SectionTitle>
        <SectionLead theme={theme}>
          Technical depth where it matters—explained clearly for everyone.
        </SectionLead>
        <FeatureGrid>
          <Card theme={theme}>
            <CardTitle theme={theme}>
              <FaDatabase color={theme.primary} aria-hidden />
              Proprietary statistics
            </CardTitle>
            <CardBody theme={theme}>
              We ingest and shape baseball data into signals that reflect real matchups:
              pitcher vulnerability, batter form, park effects, and slate context—features
              tuned for player props, not generic box scores.
            </CardBody>
            <PillRow>
              <Pill theme={theme}>Matchup splits</Pill>
              <Pill theme={theme}>Form windows</Pill>
              <Pill theme={theme}>Park & context</Pill>
            </PillRow>
          </Card>
          <Card theme={theme}>
            <CardTitle theme={theme}>
              <FaCogs color={theme.primary} aria-hidden />
              Feature engineering
            </CardTitle>
            <CardBody theme={theme}>
              Raw feeds become model-ready inputs through custom transforms. That engineering
              layer is the quiet advantage: the model only learns from features designed to
              predict prop outcomes.
            </CardBody>
            <PillRow>
              <Pill theme={theme}>Custom transforms</Pill>
              <Pill theme={theme}>Prop-specific targets</Pill>
            </PillRow>
          </Card>
          <Card theme={theme}>
            <CardTitle theme={theme}>
              <FaBrain color={theme.primary} aria-hidden />
              XGBoost models
            </CardTitle>
            <CardBody theme={theme}>
              Predictions come from gradient-boosted tree models (XGBoost) trained on
              historical outcomes for hit, total bases, multi-hit, and home run props—
              the same class of models used across serious predictive analytics.
            </CardBody>
            <PillRow>
              <Pill theme={theme}>Hit</Pill>
              <Pill theme={theme}>Total bases</Pill>
              <Pill theme={theme}>2+ hits</Pill>
              <Pill theme={theme}>HR</Pill>
            </PillRow>
          </Card>
        </FeatureGrid>
      </Section>

      <Section $delay="0.15s">
        <SectionTitle theme={theme}>Transparency you can inspect</SectionTitle>
        <SectionLead theme={theme}>
          We don&apos;t ask you to trust a black box. SHAP values show which features
          pushed a projection up or down—so you can agree, disagree, or dig deeper.
        </SectionLead>
        <TwoCol>
          <ChartCard theme={theme}>
            <CardTitle theme={theme}>
              <FaChartBar color={theme.primary} aria-hidden />
              Real SHAP breakdown
            </CardTitle>
            <ChartCaption theme={theme} style={{ marginTop: 0, marginBottom: '0.75rem' }}>
              {REAL_SHAP_EXAMPLE.playerName} · {REAL_SHAP_EXAMPLE.modelLabel} ·{' '}
              {REAL_SHAP_EXAMPLE.dateLabel}
            </ChartCaption>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart
                  data={REAL_SHAP_EXAMPLE.features}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke={theme.chartGrid} strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fill: theme.mutedText, fontSize: 11 }}
                    axisLine={{ stroke: theme.border }}
                    tickLine={false}
                    domain={[-0.12, 0.12]}
                  />
                  <YAxis
                    type="category"
                    dataKey="feature"
                    width={96}
                    tick={{ fill: theme.secondaryText, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(66, 153, 225, 0.08)' }}
                    contentStyle={{
                      background: theme.navbarBg,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      color: theme.primaryText,
                    }}
                    formatter={(value, _name, item) => [
                      Number(value).toFixed(4),
                      item?.payload?.label || 'Contribution',
                    ]}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={18}>
                    {REAL_SHAP_EXAMPLE.features.map((entry) => (
                      <Cell
                        key={entry.feature}
                        fill={entry.value >= 0 ? theme.success : theme.error}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <ChartCaption theme={theme}>
              Green lifts the projection; red pulls it down. Base value was{' '}
              {REAL_SHAP_EXAMPLE.baseValue.toFixed(3)}. Live picks show the same style of
              SHAP drivers for each player and prop.
            </ChartCaption>
          </ChartCard>

          <Card theme={theme}>
            <CardTitle theme={theme}>
              <FaBolt color={theme.primary} aria-hidden />
              Model vs. market
            </CardTitle>
            <CardBody theme={theme}>
              After the model scores a prop, we compare that probability to live odds
              across books. When our estimate exceeds the market&apos;s implied chance,
              that gap is potential edge.
            </CardBody>
            <EdgeVisual>
              <EdgeBarRow>
                <EdgeLabel theme={theme}>
                  <span>Model probability</span>
                  <span>58%</span>
                </EdgeLabel>
                <EdgeTrack theme={theme}>
                  <EdgeFill $width={edgeReady ? '58%' : '0%'} $color={theme.primary} />
                </EdgeTrack>
              </EdgeBarRow>
              <EdgeBarRow>
                <EdgeLabel theme={theme}>
                  <span>Market implied (from odds)</span>
                  <span>47%</span>
                </EdgeLabel>
                <EdgeTrack theme={theme}>
                  <EdgeFill $width={edgeReady ? '47%' : '0%'} $color={theme.warning} />
                </EdgeTrack>
              </EdgeBarRow>
              <EdgeCallout theme={theme}>+11 pts of estimated edge</EdgeCallout>
            </EdgeVisual>
            <ChartCaption theme={theme} style={{ marginTop: '1rem' }}>
              Example only. Real edges update as odds move throughout the day.
            </ChartCaption>
          </Card>
        </TwoCol>
      </Section>

      <Section $delay="0.2s">
        <SectionTitle theme={theme}>Not just &quot;ask ChatGPT who to bet&quot;</SectionTitle>
        <SectionLead theme={theme}>
          Large language models are great writers. They are not a substitute for a
          trained sports model fed with live, structured data.
        </SectionLead>
        <CompareGrid>
          <CompareColumn theme={theme}>
            <CompareHeading theme={theme}>
              <FaRobot color={theme.mutedText} aria-hidden />
              Generic ChatGPT tip
            </CompareHeading>
            <CompareList>
              <CompareItem theme={theme}>
                <FaTimesCircle color={theme.error} aria-hidden />
                Relies on training cutoff knowledge and public chatter
              </CompareItem>
              <CompareItem theme={theme}>
                <FaTimesCircle color={theme.error} aria-hidden />
                No calibrated probability for a specific prop line
              </CompareItem>
              <CompareItem theme={theme}>
                <FaTimesCircle color={theme.error} aria-hidden />
                Cannot see today&apos;s live multi-book odds or line moves
              </CompareItem>
              <CompareItem theme={theme}>
                <FaTimesCircle color={theme.error} aria-hidden />
                No SHAP trail—you get a story, not an audit
              </CompareItem>
              <CompareItem theme={theme}>
                <FaTimesCircle color={theme.error} aria-hidden />
                Same prompt tomorrow may invent a different narrative
              </CompareItem>
            </CompareList>
          </CompareColumn>
          <CompareColumn theme={theme} $accent="good">
            <CompareHeading theme={theme}>
              <FaCheckCircle color={theme.success} aria-hidden />
              Slick Picks pipeline
            </CompareHeading>
            <CompareList>
              <CompareItem theme={theme}>
                <FaCheckCircle color={theme.success} aria-hidden />
                Fresh stats, lineups, and odds ingested by scheduled Lambdas
              </CompareItem>
              <CompareItem theme={theme}>
                <FaCheckCircle color={theme.success} aria-hidden />
                XGBoost scores each prop with a model probability
              </CompareItem>
              <CompareItem theme={theme}>
                <FaCheckCircle color={theme.success} aria-hidden />
                Edge computed against live market prices
              </CompareItem>
              <CompareItem theme={theme}>
                <FaCheckCircle color={theme.success} aria-hidden />
                SHAP values show why the projection landed where it did
              </CompareItem>
              <CompareItem theme={theme}>
                <FaCheckCircle color={theme.success} aria-hidden />
                LLMs only curate—ranking and explaining pipeline output as Picks &amp; Parlays of the Day
              </CompareItem>
            </CompareList>
          </CompareColumn>
        </CompareGrid>
      </Section>

      <Section $delay="0.25s">
        <SectionTitle theme={theme}>Infrastructure that keeps it real-time</SectionTitle>
        <SectionLead theme={theme}>
          Behind the UI is a cloud pipeline of Lambdas that fetch, process, predict,
          and consolidate—so the app stays aligned with the day&apos;s data, not a static dump.
        </SectionLead>
        <InfraGrid>
          <InfraNode theme={theme}>
            <InfraIcon theme={theme}><FaDatabase aria-hidden /></InfraIcon>
            <InfraTitle theme={theme}>Stats &amp; lineups</InfraTitle>
            <InfraDesc theme={theme}>Lambdas pull and refresh player and game context</InfraDesc>
          </InfraNode>
          <InfraNode theme={theme}>
            <InfraIcon theme={theme}><FaBrain aria-hidden /></InfraIcon>
            <InfraTitle theme={theme}>Predictions + SHAP</InfraTitle>
            <InfraDesc theme={theme}>Models score props and store feature attributions</InfraDesc>
          </InfraNode>
          <InfraNode theme={theme}>
            <InfraIcon theme={theme}><FaBolt aria-hidden /></InfraIcon>
            <InfraTitle theme={theme}>Live odds</InfraTitle>
            <InfraDesc theme={theme}>Multi-book prices land so edge can be calculated</InfraDesc>
          </InfraNode>
          <InfraNode theme={theme}>
            <InfraIcon theme={theme}><FaCloud aria-hidden /></InfraIcon>
            <InfraTitle theme={theme}>Consolidate</InfraTitle>
            <InfraDesc theme={theme}>Results are packaged for the API and product UI</InfraDesc>
          </InfraNode>
          <InfraNode theme={theme}>
            <InfraIcon theme={theme}><FaMagic aria-hidden /></InfraIcon>
            <InfraTitle theme={theme}>GenAI curation</InfraTitle>
            <InfraDesc theme={theme}>LLM reviews pipeline output for top picks &amp; parlays</InfraDesc>
          </InfraNode>
        </InfraGrid>
      </Section>

      <Section $delay="0.3s">
        <SectionTitle theme={theme}>LLMs where they help—not where they guess</SectionTitle>
        <SectionLead theme={theme}>
          After models and odds do the hard math, an LLM inspects that structured output
          to surface the clearest opportunities of the day.
        </SectionLead>
        <TwoCol>
          <Card theme={theme}>
            <CardTitle theme={theme}>
              <FaMagic color={theme.primary} aria-hidden />
              Picks of the Day
            </CardTitle>
            <CardBody theme={theme}>
              Top singles are highlighted from the scored slate so you can act quickly
              without scanning every prop table yourself.
            </CardBody>
          </Card>
          <Card theme={theme}>
            <CardTitle theme={theme}>
              <FaMagic color={theme.primary} aria-hidden />
              Parlay of the Day
            </CardTitle>
            <CardBody theme={theme}>
              Combinations are suggested from the same pipeline evidence—curated for
              readability, grounded in model + odds data rather than invented narratives.
            </CardBody>
          </Card>
        </TwoCol>
      </Section>

      <Section $delay="0.35s">
        <CtaSection theme={theme}>
          <CtaTitle theme={theme}>See the pipeline in action</CtaTitle>
          <CtaText theme={theme}>
            Browse today&apos;s projections, open SHAP insights on a pick, and check
            how model probability stacks up against the market.
          </CtaText>
          <CtaButton theme={theme} type="button" onClick={handleGetStarted}>
            {isAuthenticated ? 'Go to dashboard' : 'Create a free account'}
          </CtaButton>
          <SecondaryLink theme={theme} type="button" onClick={() => navigate('/game-picks')}>
            Or jump straight to Game Picks
          </SecondaryLink>
        </CtaSection>
      </Section>
    </Page>
  );
};

export default HowItWorksPage;
